import type { RequestInfo } from '@ember-data/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';

/**
 * Request Builders for OpenSearch Operations
 *
 * These builders provide typed access to search, mapping, stats,
 * and other OpenSearch-related endpoints.
 *
 * For OpenSearch response types (OpenSearchHit, OpenSearchHits, OpenSearchResponse),
 * import from 'tio-common/models/search' to avoid duplication.
 */

/**
 * Response types for search endpoints
 */
export interface SearchMappingResponse {
  [key: string]: unknown;
}

export interface SearchStatsResponse {
  index: string;
  docCount: number;
  size: string;
  sizeInBytes: number;
  status: 'healthy' | 'warning' | 'critical';
  rawStats: {
    health: Record<string, unknown>;
    stats: Record<string, unknown>;
  };
}

export interface SearchSettingsResponse {
  [key: string]: unknown;
}

export interface SearchExplainResponse {
  [key: string]: unknown;
}

export interface FieldAnalysis {
  field: string;
  type: string;
  sampleValue?: unknown;
  currentType?: string;
  newType?: string;
}

export interface SearchDataPreviewResponse {
  recordType: string;
  recordId: string;
  searchData: Record<string, unknown>;
  analysis: {
    newFields: FieldAnalysis[];
    existingFields: FieldAnalysis[];
    typeConflicts: FieldAnalysis[];
    totalFields: number;
    currentMappingFieldCount: number;
  };
}

/**
 * Get the OpenSearch mapping for a model type
 *
 * @param modelType - The model type (Person, PslfForm, TasApplication)
 *
 * @example
 * const { content } = await store.request(getSearchMappingRequest('Person'));
 */
export function getSearchMappingRequest(modelType: string): RequestInfo {
  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'search' },
    op: 'query',
    resourcePath: 'api/v2/search',
  };
  const url = buildBaseURL(urlOptions);

  return {
    url: `${url}/mapping?model-type=${modelType}`,
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Get OpenSearch index statistics for a model type
 *
 * @param modelType - The model type (Person, PslfForm, TasApplication)
 *
 * @example
 * const { content } = await store.request(getSearchStatsRequest('Person'));
 */
export function getSearchStatsRequest(modelType: string): RequestInfo {
  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'search' },
    op: 'query',
    resourcePath: 'api/v2/search',
  };
  const url = buildBaseURL(urlOptions);

  return {
    url: `${url}/stats?model-type=${modelType}`,
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Get OpenSearch index settings for a model type
 *
 * @param modelType - The model type (Person, PslfForm, TasApplication)
 *
 * @example
 * const { content } = await store.request(getSearchSettingsRequest('Person'));
 */
export function getSearchSettingsRequest(modelType: string): RequestInfo {
  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'search' },
    op: 'query',
    resourcePath: 'api/v2/search',
  };
  const url = buildBaseURL(urlOptions);

  return {
    url: `${url}/settings?model-type=${modelType}`,
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Execute an OpenSearch query
 *
 * @param query - The search query object
 *
 * @example
 * const { content } = await store.request(executeSearchRequest({
 *   filter: { 'model-type': 'Person' },
 *   query: { match_all: {} }
 * }));
 */
export function executeSearchRequest(query: Record<string, unknown>): RequestInfo {
  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'search' },
    op: 'query',
    resourcePath: 'api/v2/search',
  };
  const url = buildBaseURL(urlOptions);

  return {
    url,
    method: 'POST',
    body: JSON.stringify(query),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Explain how a search query would execute
 *
 * @param query - The search query object
 *
 * @example
 * const { content } = await store.request(explainSearchRequest({
 *   filter: { 'model-type': 'Person' },
 *   query: { match_all: {} }
 * }));
 */
export function explainSearchRequest(query: Record<string, unknown>): RequestInfo {
  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'search' },
    op: 'query',
    resourcePath: 'api/v2/search',
  };
  const url = buildBaseURL(urlOptions);

  return {
    url: `${url}/explain`,
    method: 'POST',
    body: JSON.stringify(query),
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Preview what search_data would look like for a record and compare to current mapping
 *
 * @param modelType - The model type (Person, PslfForm, TasApplication)
 * @param recordId - The ID of the record to preview
 *
 * @example
 * const { content } = await store.request(previewSearchDataRequest('Person', '172654'));
 * const preview = content as SearchDataPreviewResponse;
 * console.log(`New fields: ${preview.analysis.newFields.length}`);
 * console.log(`Type conflicts: ${preview.analysis.typeConflicts.length}`);
 */
export function previewSearchDataRequest(modelType: string, recordId: string): RequestInfo {
  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'search' },
    op: 'query',
    resourcePath: 'api/v2/search',
  };
  const url = buildBaseURL(urlOptions);

  return {
    url: `${url}/preview?model-type=${modelType}&record-id=${recordId}`,
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Trigger a full reindex for a model type
 *
 * @param type - The model type to reindex
 *
 * @example
 * const { content } = await store.request(rebuildIndexRequest('Person'));
 */
export function rebuildIndexRequest(type: string): RequestInfo {
  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'admin' },
    op: 'query',
    resourcePath: 'api/v2/admin',
  };
  const url = buildBaseURL(urlOptions);

  return {
    url: `${url}/reindex?type=${type}`,
    method: 'GET',
    cacheOptions: { [SkipCache]: true },
  };
}

/**
 * Reindex a specific record
 *
 * @param modelType - The model type
 * @param recordId - The ID of the record to reindex
 *
 * @example
 * const { content } = await store.request(rebuildRecordIndexRequest('Person', '123'));
 */
export function rebuildRecordIndexRequest(modelType: string, recordId: string): RequestInfo {
  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'admin' },
    op: 'query',
    resourcePath: 'api/v2/admin',
  };
  const url = buildBaseURL(urlOptions);

  return {
    url: `${url}/reindex-record`,
    method: 'POST',
    body: JSON.stringify({
      model_type: modelType,
      record_id: recordId,
    }),
    cacheOptions: { [SkipCache]: true },
  };
}
